import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import {
  CreateCostItem,
  UpdateCostItem,
  Category,
  Unit,
  CostItemGroup,
  Supplier,
  CreateCostItemSupplier,
} from '../types/api';
import SupplierPricingDialog from './SupplierPricingDialog';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';

interface CostItemDialogProps {
  open: boolean;
  onClose: () => void;
  costItemId?: number;
  onSuccess: () => void;
  onError?: (message: string) => void;
}

interface SupplierRow {
  id: string;
  costItemSupplierId?: number;
  supplierId: number | '';
  unitCost: number | '';
  effectiveDate: string;
}

const sortSupplierRows = (rows: SupplierRow[], suppliers: Supplier[]): SupplierRow[] => {
  const supplierNameById = new Map<number, string>(
    suppliers.map((supplier) => [supplier.supplierId, supplier.supplierName.toLowerCase()])
  );

  return [...rows].sort((a, b) => {
    const aTime = new Date(a.effectiveDate).getTime();
    const bTime = new Date(b.effectiveDate).getTime();
    const normalizedA = Number.isFinite(aTime) ? aTime : 0;
    const normalizedB = Number.isFinite(bTime) ? bTime : 0;
    if (normalizedA !== normalizedB) {
      return normalizedB - normalizedA;
    }

    const aCost = typeof a.unitCost === 'number' ? a.unitCost : Number(a.unitCost);
    const bCost = typeof b.unitCost === 'number' ? b.unitCost : Number(b.unitCost);
    const normalizedACost = Number.isFinite(aCost) ? aCost : Number.POSITIVE_INFINITY;
    const normalizedBCost = Number.isFinite(bCost) ? bCost : Number.POSITIVE_INFINITY;
    if (normalizedACost !== normalizedBCost) {
      return normalizedACost - normalizedBCost;
    }

    const aSupplierName =
      typeof a.supplierId === 'number' ? supplierNameById.get(a.supplierId) ?? '' : '';
    const bSupplierName =
      typeof b.supplierId === 'number' ? supplierNameById.get(b.supplierId) ?? '' : '';
    if (aSupplierName !== bSupplierName) {
      return aSupplierName.localeCompare(bSupplierName);
    }

    return a.id.localeCompare(b.id);
  });
};

const CostItemDialog: React.FC<CostItemDialogProps> = ({
  open,
  onClose,
  costItemId,
  onSuccess,
  onError,
}) => {
  const selectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: appColors.elevationDark,
        border: `1px solid ${appColors.borderDark}`,
        color: 'white',
        '& .MuiMenuItem-root': {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#e2e8f0',
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(59, 130, 246, 0.3)',
            },
          },
        },
      },
    },
  } as const;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [costItemGroups, setCostItemGroups] = useState<CostItemGroup[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [formData, setFormData] = useState({
    itemCode: '',
    itemName: '',
    description: '',
    categoryId: 0,
    costItemGroupId: 0,
    unitId: 0,
    outputRate: '',
    outputRateUnitId: 0,
  });
  const [supplierRows, setSupplierRows] = useState<SupplierRow[]>([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierRow | null>(null);

  const isEditMode = !!costItemId;

  useEffect(() => {
    if (open) {
      loadDropdownData();
      if (isEditMode && costItemId) {
        loadCostItem(costItemId);
      } else {
        resetForm();
      }
    }
  }, [open, costItemId, isEditMode]);

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      const [cats, unitsData, suppliersData] = await Promise.all([
        apiService.getCategories(),
        apiService.getUnits(),
        apiService.getSuppliers(),
      ]);
      setCategories(cats);
      setUnits(unitsData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      onError?.('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  };

  const loadCostItemGroups = async (categoryId: number) => {
    try {
      const groups = await apiService.getCostItemGroups(categoryId);
      setCostItemGroups(groups);
    } catch (error) {
      console.error('Error loading cost item groups:', error);
    }
  };

  const loadCostItem = async (id: number) => {
    try {
      setLoadingData(true);
      const item = await apiService.getCostItem(id);
      setFormData({
        itemCode: item.itemCode,
        itemName: item.itemName,
        description: item.description || '',
        categoryId: item.categoryId,
        costItemGroupId: item.costItemGroupId || 0,
        unitId: item.unitId,
        outputRate: item.outputRate?.toString() || '',
        outputRateUnitId: item.outputRateUnitId || 0,
      });

      // Load cost item groups for the selected category
      if (item.categoryId) {
        await loadCostItemGroups(item.categoryId);
      }

      // Set supplier rows
      const loadedRows = item.suppliers.map((supplier) => ({
          costItemSupplierId: supplier.costItemSupplierId,
          id: `existing-${supplier.costItemSupplierId}`,
          supplierId: supplier.supplierId,
          unitCost: supplier.unitCost,
          effectiveDate: supplier.effectiveDate.split('T')[0], // Format date
      }));
      setSupplierRows(sortSupplierRows(loadedRows, suppliers));
    } catch (error) {
      console.error('Error loading cost item:', error);
      onError?.('Failed to load cost item');
      onClose();
    } finally {
      setLoadingData(false);
    }
  };

  const resetForm = () => {
    setFormData({
      itemCode: '',
      itemName: '',
      description: '',
      categoryId: 0,
      costItemGroupId: 0,
      unitId: 0,
      outputRate: '',
      outputRateUnitId: 0,
    });
    setSupplierRows([]);
    setSupplierSearchTerm('');
    setCostItemGroups([]);
  };

  const handleCategoryChange = async (categoryId: number) => {
    setFormData({ ...formData, categoryId, costItemGroupId: 0 });
    if (categoryId > 0) {
      await loadCostItemGroups(categoryId);
    } else {
      setCostItemGroups([]);
    }
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setSupplierDialogOpen(true);
  };

  const handleEditSupplier = (row: SupplierRow) => {
    setEditingSupplier(row);
    setSupplierDialogOpen(true);
  };

  const handleRemoveSupplier = (id: string) => {
    setSupplierRows(supplierRows.filter((row) => row.id !== id));
  };

  const handleSupplierDialogSave = (supplierId: number, unitCost: number, effectiveDate: string) => {
    if (editingSupplier) {
      // Update existing supplier
      const updatedRows = supplierRows.map((row) =>
          row.id === editingSupplier.id
            ? { ...row, supplierId, unitCost, effectiveDate }
            : row
        );
      setSupplierRows(sortSupplierRows(updatedRows, suppliers));
    } else {
      // Add new supplier
      const newRows = [
        ...supplierRows,
        {
          id: `new-${Date.now()}`,
          supplierId,
          unitCost,
          effectiveDate,
        },
      ];
      setSupplierRows(sortSupplierRows(newRows, suppliers));
    }
    setEditingSupplier(null);
  };

  useEffect(() => {
    setSupplierRows((prev) => sortSupplierRows(prev, suppliers));
  }, [suppliers]);

  const filteredSupplierRows = useMemo(() => {
    const query = supplierSearchTerm.trim().toLowerCase();
    if (!query) return supplierRows;

    return supplierRows.filter((row) => {
      const supplierName = suppliers.find((s) => s.supplierId === row.supplierId)?.supplierName ?? '';
      const priceText =
        row.unitCost !== '' && row.unitCost !== null && row.unitCost !== undefined
          ? formatCurrency(Number(row.unitCost))
          : '';
      const rawPriceText =
        row.unitCost !== '' && row.unitCost !== null && row.unitCost !== undefined
          ? String(row.unitCost)
          : '';
      const dateText = row.effectiveDate ? new Date(row.effectiveDate).toLocaleDateString() : '';

      return (
        supplierName.toLowerCase().includes(query) ||
        priceText.toLowerCase().includes(query) ||
        rawPriceText.toLowerCase().includes(query) ||
        row.effectiveDate.toLowerCase().includes(query) ||
        dateText.toLowerCase().includes(query)
      );
    });
  }, [supplierRows, suppliers, supplierSearchTerm]);

  const handleSupplierDialogClose = () => {
    setSupplierDialogOpen(false);
    setEditingSupplier(null);
  };

  const validateForm = (): boolean => {
    if (!formData.itemCode.trim()) {
      onError?.('Item Code is required');
      return false;
    }
    if (!formData.itemName.trim()) {
      onError?.('Item Name is required');
      return false;
    }
    if (!formData.categoryId) {
      onError?.('Category is required');
      return false;
    }
    if (!formData.unitId) {
      onError?.('Unit is required');
      return false;
    }

    // Validate supplier rows
    for (const row of supplierRows) {
      if (!row.supplierId) {
        onError?.('All supplier rows must have a supplier selected');
        return false;
      }
      if (!row.unitCost || Number(row.unitCost) <= 0) {
        onError?.('All supplier rows must have a valid price');
        return false;
      }
      if (!row.effectiveDate) {
        onError?.('All supplier rows must have an effective date');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const supplierData: CreateCostItemSupplier[] = supplierRows.map((row) => ({
        supplierId: Number(row.supplierId),
        unitCost: Number(row.unitCost),
        effectiveDate: row.effectiveDate,
      }));

      if (isEditMode && costItemId) {
        const updateData: UpdateCostItem = {
          itemCode: formData.itemCode,
          itemName: formData.itemName,
          description: formData.description || undefined,
          categoryId: formData.categoryId,
          costItemGroupId: formData.costItemGroupId || undefined,
          unitId: formData.unitId,
          outputRate: formData.outputRate ? Number(formData.outputRate) : undefined,
          outputRateUnitId: formData.outputRateUnitId || undefined,
          suppliers: supplierData,
        };
        await apiService.updateCostItem(costItemId, updateData);
      } else {
        const createData: CreateCostItem = {
          itemCode: formData.itemCode,
          itemName: formData.itemName,
          description: formData.description || undefined,
          categoryId: formData.categoryId,
          costItemGroupId: formData.costItemGroupId || undefined,
          unitId: formData.unitId,
          outputRate: formData.outputRate ? Number(formData.outputRate) : undefined,
          outputRateUnitId: formData.outputRateUnitId || undefined,
          suppliers: supplierData,
        };
        await apiService.createCostItem(createData);
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error saving cost item:', error);
      onError?.(error.response?.data?.message || error.message || 'Failed to save cost item');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={listPageDialogPaperProps}
    >
      <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
        {isEditMode ? 'Edit Cost Item' : 'Add New Cost Item'}
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <div className="pt-1 space-y-6">
            {/* Item Information Section */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-2 border-b border-border-dark">
                Item Information
              </h3>
              <div className="space-y-4">
                <TextField
                  autoFocus
                  margin="dense"
                  label="Item Code"
                  fullWidth
                  variant="outlined"
                  required
                  value={formData.itemCode}
                  onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Item Name"
                  fullWidth
                  variant="outlined"
                  required
                  value={formData.itemName}
                  onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Item Properties Section */}
            <div>
              <h3 className="text-lg font-bold mb-4 pb-2 border-b border-border-dark">
                Item Properties
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.categoryId}
                      label="Category"
                      onChange={(e) => handleCategoryChange(Number(e.target.value))}
                      MenuProps={selectMenuProps}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                          {cat.categoryName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Cost Item Group</InputLabel>
                    <Select
                      value={formData.costItemGroupId}
                      label="Cost Item Group"
                      onChange={(e) =>
                        setFormData({ ...formData, costItemGroupId: Number(e.target.value) })
                      }
                      disabled={!formData.categoryId || costItemGroups.length === 0}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value={0}>
                        <em>None</em>
                      </MenuItem>
                      {costItemGroups.map((group) => (
                        <MenuItem key={group.costItemGroupId} value={group.costItemGroupId}>
                          {group.groupName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={formData.unitId}
                      label="Unit"
                      onChange={(e) => setFormData({ ...formData, unitId: Number(e.target.value) })}
                      MenuProps={selectMenuProps}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit.unitId} value={unit.unitId}>
                          {unit.unitName} {unit.unitSymbol ? `(${unit.unitSymbol})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    margin="dense"
                    label="Output Rate"
                    fullWidth
                    variant="outlined"
                    type="number"
                    value={formData.outputRate}
                    onChange={(e) => setFormData({ ...formData, outputRate: e.target.value })}
                  />
                </div>

                <FormControl fullWidth>
                  <InputLabel>Output Rate Unit</InputLabel>
                  <Select
                    value={formData.outputRateUnitId}
                    label="Output Rate Unit"
                    onChange={(e) =>
                      setFormData({ ...formData, outputRateUnitId: Number(e.target.value) })
                    }
                    disabled={!formData.outputRate}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value={0}>
                      <em>None</em>
                    </MenuItem>
                    {units.map((unit) => (
                      <MenuItem key={unit.unitId} value={unit.unitId}>
                        {unit.unitName} {unit.unitSymbol ? `(${unit.unitSymbol})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* Supplier Pricing Section */}
            <div>
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-border-dark">
                <h3 className="text-lg font-bold">Supplier Pricing</h3>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddSupplier}
                  sx={{
                    backgroundColor: appColors.primary,
                    '&:hover': {
                      backgroundColor: appColors.primary,
                      opacity: 0.9,
                    },
                  }}
                >
                  Add Supplier
                </Button>
              </div>

              {supplierRows.length === 0 ? (
                <p className="text-gray-400 text-sm py-4">No suppliers added. Click "Add Supplier" to add one.</p>
              ) : (
                <div className="space-y-3">
                  <TextField
                    size="small"
                    fullWidth
                    label="Search supplier or price/date"
                    value={supplierSearchTerm}
                    onChange={(e) => setSupplierSearchTerm(e.target.value)}
                    placeholder="Type to filter list"
                  />
                  {filteredSupplierRows.map((row) => {
                    const supplier = suppliers.find((s) => s.supplierId === row.supplierId);
                    return (
                      <div
                        key={row.id}
                        className="bg-border-dark/60 rounded-lg p-4 border border-border-dark hover:border-primary transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm text-muted mb-1">Supplier Name</p>
                              <p className="text-white font-medium">
                                {supplier?.supplierName || 'Not selected'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-muted mb-1">Price</p>
                              <p className="text-white font-medium">
                                {row.unitCost !== '' && row.unitCost !== null && row.unitCost !== undefined
                                  ? formatCurrency(Number(row.unitCost))
                                  : 'Not set'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-muted mb-1">Effective Date</p>
                              <p className="text-white font-medium">
                                {row.effectiveDate
                                  ? new Date(row.effectiveDate).toLocaleDateString()
                                  : 'Not set'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <IconButton
                              size="small"
                              onClick={() => handleEditSupplier(row)}
                              sx={{
                                color: appColors.primary,
                                '&:hover': {
                                  backgroundColor: appColors.primary,
                                  color: 'white',
                                },
                              }}
                            >
                              <span className="material-symbols-outlined text-base">edit</span>
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSupplier(row.id)}
                              sx={{
                                color: '#ef4444',
                                '&:hover': {
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                },
                              }}
                            >
                              <span className="material-symbols-outlined text-base">delete</span>
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {supplierRows.length > 0 && filteredSupplierRows.length === 0 && (
                    <p className="text-gray-400 text-sm py-2">No supplier quotes match your search.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            color: appColors.mutedText,
            borderColor: appColors.dialogBorder,
            '&:hover': {
              borderColor: appColors.primary,
              color: appColors.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingData}
          sx={{
            backgroundColor: appColors.primary,
            '&:hover': {
              backgroundColor: appColors.primary,
              opacity: 0.9,
            },
          }}
        >
          {loading ? <CircularProgress size={20} /> : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>

      {/* Supplier Pricing Dialog */}
      <SupplierPricingDialog
        open={supplierDialogOpen}
        onClose={handleSupplierDialogClose}
        onSave={handleSupplierDialogSave}
        suppliers={suppliers}
        initialData={
          editingSupplier && editingSupplier.supplierId && editingSupplier.unitCost && editingSupplier.effectiveDate
            ? {
                supplierId: Number(editingSupplier.supplierId),
                unitCost: Number(editingSupplier.unitCost),
                effectiveDate: editingSupplier.effectiveDate,
              }
            : undefined
        }
      />
    </Dialog>
  );
};

export default CostItemDialog;

